import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, CircularProgress } from "@mui/material";
import { showNotesPrompt } from "../../components/NotesPromptDialog/notesPrompt";
import AddPaymentWidget from "./../../components/AddPaymentWidget";

import OrdersListWidget from "../../components/AddPaymentWidget/OrdersListWidget";
import {
    useCrateOrderInvoice,
    useOrderInvoiceData,
} from "../../shared/hooks/useInvoicesData";

import _ from "lodash";
import { enqueueSnackbar } from "notistack";
import { pdfjs } from "react-pdf";
import { useTranslation } from "react-i18next";
import { AppContext } from "../../AppContext";
import { useUpdateDoctypeData } from "../../shared/hooks/useDocTypesData";
import { useAddons } from "../../shared/hooks/useItemsData";
import {
    useDefferOrderItem,
    useDivideOrderItemsData,
    useOrderItemOnHouse,
} from "../../shared/hooks/useTableOrderItemsData";
import {
    useFetchGetInvoiceData,
    useFetchTableOrder,
    useHandleStaffMeal,
} from "../../shared/hooks/useTableOrdersData";
import {
    calculateLoyaltyRedemptionAmount,
    useCustomerLoyaltyProgram,
} from "../../shared/hooks/useLoyaltyData";
import { useGetDocTypeList } from "../../shared/hooks/useDocTypesData";
import { useGetDocTypeDetails } from "../../shared/hooks/useFrappeDoctype";
import { usePrintOrder } from "../../shared/hooks/useTablesData";
import { PosContext } from "../../shared/PosContext";
import { useMasterPassword } from "../../components/MasterPasswordDialog/MasterPasswordContext";
import { isJsonString } from "../../utils/floorUtils";
import {
    buildDownloadPdfUrl,
    getPosInvoicePrintFormat,
    getProfileLetterhead,
    getTableOrderPrintFormat,
} from "../../utils/printFormatUrl";
import { buildDiscountPayload } from "../../utils/discountOptions";

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

export default function WidgetsPage({
    dialogOpen,
    mode,
    handleNext,
    backCallback,
    table,
    order,
    handleInvoiceClosed,
    isTakeaway,
}) {
    const { t } = useTranslation();
    const {
        setLoadingTable,
        restaurantSettings,
        elevatedPrivileges,
        isRetailPos,
        posProfile,
    } = useContext(AppContext);
    const selectedLetterhead = getProfileLetterhead(posProfile);
    const tableOrderPrintFormat = getTableOrderPrintFormat(posProfile);
    const invoicePrintFormat = isRetailPos
        ? getPosInvoicePrintFormat(posProfile)
        : "POS";

    const { refetchTablesData, posProfileCompanyMismatch } = useContext(PosContext);
    const {
        data: addons,
        isFetching: isAddonsFetching,
        isError: isAddonsError,
        refetch: refetchAddons,
    } = useAddons();

    const [discount, setDiscount] = useState(0);
    const [tip, setTip] = useState(0);
    const [invoice, setInvoice] = useState(null);
    const [isPaymentLoading, setPaymentLoading] = useState(false);
    const [payingAll, setPayingAll] = useState(false);

    const [finalPayload, setFinalPayload] = useState();
    const [amountAfterDiscount, setAmountAfterDiscount] = useState("00.00");

    const [paymentType, setPaymentType] = useState(null);

    const [presetDiscount, setPresetDiscount] = useState(null);
    const [presetDiscountAmount, setPresetDiscountAmount] = useState(null);
    const [orderOnHouse, setOrderOnHouse] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(false);

    const [amountChanged, setAmountChanged] = useState(false);

    const [fixedDiscountAmount, setFixedDiscountAmount] = useState(0);
    const [discountPercentage, setDiscountPercentage] = useState(0);

    const [paymentData, setPaymentData] = useState({});

    const [orderEntryItems, setOrderEntryItems] = useState({});
    const [redeemLoyaltyPoints, setRedeemLoyaltyPoints] = useState(false);
    const [redeemedPoints, setRedeemedPoints] = useState(0);

    // When restaurant settings have `skip_divide_invoice` enabled we want to
    // bypass the OrdersListWidget (divide-invoice) screen entirely and jump
    // straight to the payment screen with the full order being paid.
    const skipDivideInvoice =
        isRetailPos ||
        restaurantSettings?.docs?.[0]?.skip_divide_invoice == 1;
    // Guard so the auto-advance only fires once per dialog session
    // (otherwise going back from the payment screen would immediately
    // re-trigger and the user could never escape).
    const autoAdvancedRef = useRef(false);
    const invoicePreviewRequestRef = useRef(0);
    const [isAutoAdvancing, setIsAutoAdvancing] = useState(false);
    const [autoAdvanceError, setAutoAdvanceError] = useState(false);

    const approveOnCashClose =
        Number(restaurantSettings?.docs?.[0]?.approve_on_cash_close) === 1;
    const activeOrderName =
        order?.name || Object.values(table?.orders || {})[0]?.name || null;

    const { data: model, isLoading: isInvoiceLoading } = useOrderInvoiceData(
        activeOrderName
    );

    const updateTableOrder = useUpdateDoctypeData();
    const printOrder = usePrintOrder();
    const {
        mutate: fetchGetInvoiceData,
        mutateAsync: fetchGetInvoiceDataAsync,
        isLoading: isInvoiceDataLoading,
    } = useFetchGetInvoiceData();
    const handleStaffMealMutation = useHandleStaffMeal();
    const fetchTableOrderMutation = useFetchTableOrder();

    const { data: staffMealEmployees } = useGetDocTypeList({
        qKey: ["GetStaffMealEmployees"],
        doctype: "Employee",
        filters: [["status", "=", "Active"]],
        fields: ["name", "employee_name", "user_id"],
        sortBy: "employee_name asc",
        limit: 0,
    });

    const { data: loyaltyProgramsList } = useGetDocTypeList({
        qKey: ["GetActiveLoyaltyPrograms"],
        doctype: "Loyalty Program",
        fields: ["name", "from_date", "to_date"],
        limit: 0,
    });

    const deliveryProviderName = useMemo(
        () => String(order?.delivery_provider || "").trim(),
        [order?.delivery_provider]
    );
    const { data: deliveryProvider } = useGetDocTypeDetails(
        "Delivery Providers",
        deliveryProviderName
    );
    const providerAllowsDiscount =
        Number(deliveryProvider?.allow_discount) === 1;

    const hasActiveLoyaltyProgram = useMemo(() => {
        if (!Array.isArray(loyaltyProgramsList) || loyaltyProgramsList.length === 0) {
            return false;
        }
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return loyaltyProgramsList.some((program) => {
            const fromDate = program?.from_date ? new Date(program.from_date) : null;
            const toDate = program?.to_date ? new Date(program.to_date) : null;
            const startedOk = !fromDate || fromDate <= today;
            const notExpired = !toDate || toDate >= today;
            return startedOk && notExpired;
        });
    }, [loyaltyProgramsList]);

    const entryItems = useMemo(() => {
        const entryItemsFromOrder = model?.doc?.entry_items || [];
        const addonItems = (addons || []).filter(
            (item) => item.item_type === "Addons"
        );

        return _.differenceWith(
            entryItemsFromOrder,
            addonItems,
            (entryItem, tempItem) =>
                _.toLower(entryItem.item_code) ===
                _.toLower(tempItem.item_code)
        );
    }, [model, addons]);

    const entryItemsPayload = useMemo(() => {
        const itemsPayload = {};
        entryItems?.forEach((item) => {
            // NOTE: the per-entry `identifier` field is intentionally NOT
            // included in the payload sent to `get_invoice` / `divide`.
            // The backend routes by the outer object key
            // (`itemsPayload[item.identifier]`); the duplicated inner field
            // only caused confusion (e.g. split rows generating a fresh
            // uuid that didn't match the original entry).
            itemsPayload[item.identifier] = {
                item_code: item.item_code,
                qty: item.qty,
                rate: item.rate,
                price_list_rate: item.rate,
                notes: item.notes,
                addons: item.addons,
                has_serial_no: 0,
                serial_no: 0,
                has_batch_no: 0,
                batch_no: 0,
            };
        });
        return itemsPayload;
    }, [entryItems]);

    const loyaltyCustomer = useMemo(() => {
        return selectedCustomer || invoice?.customer || order?.customer || null;
    }, [selectedCustomer, invoice?.customer, order?.customer]);

    const loyaltyQuery = useCustomerLoyaltyProgram(
        loyaltyCustomer,
        posProfile?.company,
        {
            enabled: !!mode,
        }
    );
    const loyalty = loyaltyQuery.data;
    const loyaltyAmount = useMemo(() => {
        if (!redeemLoyaltyPoints || redeemedPoints <= 0) {
            return 0;
        }

        return calculateLoyaltyRedemptionAmount(
            redeemedPoints,
            loyalty?.conversionFactor
        );
    }, [redeemLoyaltyPoints, redeemedPoints, loyalty?.conversionFactor]);

    useEffect(() => {
        if (order) {
            if (order.discount) {
                const totalBeforeDiscount = order.discount + order.amount;
                // Preserve the order's actual preset discount. Do NOT round to
                // the nearest 5 / cap at 20% here: that is only meant for the
                // manual percentage buttons. Capping a preset discount (e.g.
                // 30%) to 20% sends a wrong discount_percentage to the backend
                // and produces incorrect totals.
                const _discount =
                    Math.round(
                        (1 - order.amount / totalBeforeDiscount) * 100 * 100
                    ) / 100;
                setPresetDiscount(_discount);
                setPresetDiscountAmount(order.discount); // Store the actual discount amount
                setDiscount(_discount);
                setPaymentData({
                    discount: _discount,
                    tip: 0,
                    amount: totalBeforeDiscount,
                    amountAfterDiscount: order.amount,
                    mode_of_payment: paymentType,
                    orderAmount: order.amount,
                    entryItems: [],
                });
            } else {
                setPresetDiscount(null);
                setPresetDiscountAmount(null);
                setPaymentData({
                    discount: 0,
                    tip: 0,
                    amount: order.amount,
                    amountAfterDiscount: order.amount,
                    mode_of_payment: paymentType,
                    orderAmount: order.amount,
                    entryItems: [],
                });
            }
        }
    }, [order]);

    const callHandleNext = (totalPayload) => {
        if (!activeOrderName) {
            setIsAutoAdvancing(false);
            setAutoAdvanceError(true);
            return;
        }
        let invoiceEntriesPayload = {};
        if (totalPayload.payingAll) {
            invoiceEntriesPayload = entryItemsPayload;
            setPayingAll(true);
        } else {
            Object.entries(totalPayload.items).forEach(([key, value]) => {
                // When splitting payment, send an empty addons array. The
                // original entry's addons string carries the full order qty
                // (e.g. 2) and would be sent as-is even when paying a single
                // unit, producing an invoice with wrong addon qty. The
                // backend still requires the `addons` key to exist on each
                // entry, so we pass "[]" rather than omitting it.
                invoiceEntriesPayload[key] = {
                    ...(entryItemsPayload[key] || {}),
                    ...value,
                    addons: "[]",
                };
            });
            setPayingAll(false);
        }

        setOrderEntryItems(invoiceEntriesPayload);

        const _addonsEntries = {};

        Object.values(invoiceEntriesPayload).forEach((value) => {
            const entryAddons = isJsonString(value.addons)
                ? JSON.parse(value.addons)
                : value.addons;
            if (!Array.isArray(entryAddons)) return;
            entryAddons.forEach((addon) => {
                _addonsEntries[addon.identifier] = {
                    ...addon,
                    batch_no: 0,
                    addons: "[]",
                    has_batch_no: 0,
                    has_serial_no: 0,
                    serial_no: 0,
                    price_list_rate: addon.rate,
                };
            });
        });

        setInvoice(null);
        const requestId = ++invoicePreviewRequestRef.current;
        fetchGetInvoiceData(
            {
                orderId: activeOrderName,
                entry_items: { ...invoiceEntriesPayload },
                // entry_items: { ...invoiceEntriesPayload, ..._addonsEntries },
                discount_percentage: discount,
            },
            {
                onSuccess: (data) => {
                    if (invoicePreviewRequestRef.current !== requestId) return;
                    setInvoice(data);
                    setIsAutoAdvancing(false);
                    setAutoAdvanceError(false);
                    handleNext(finalPayload);
                },
                onError: () => {
                    if (invoicePreviewRequestRef.current !== requestId) return;
                    setIsAutoAdvancing(false);
                    setAutoAdvanceError(true);
                    autoAdvancedRef.current = false;
                },
            }
        );
    };

    useEffect(() => {
        if (!dialogOpen) {
            invoicePreviewRequestRef.current += 1;
            autoAdvancedRef.current = false;
            setIsAutoAdvancing(false);
            setAutoAdvanceError(false);
            setPaymentLoading(false);
        }
    }, [dialogOpen]);

    // Auto-skip the divide-invoice screen when the restaurant setting is on or retail POS is active.
    // Wait until entryItems are loaded so callHandleNext has a real payload
    // to compute the full-pay invoice from.
    useEffect(() => {
        if (
            !skipDivideInvoice ||
            mode || // already past the divide screen
            autoAdvanceError ||
            autoAdvancedRef.current ||
            isAddonsFetching ||
            isAddonsError ||
            !Array.isArray(addons) ||
            !entryItems ||
            entryItems.length === 0 ||
            isInvoiceLoading ||
            isInvoiceDataLoading
        ) {
            return;
        }
        autoAdvancedRef.current = true;
        setIsAutoAdvancing(true);
        callHandleNext({ items: {}, payingAll: true });
    }, [
        skipDivideInvoice,
        mode,
        entryItems,
        isInvoiceLoading,
        isInvoiceDataLoading,
        autoAdvanceError,
        isAddonsFetching,
        isAddonsError,
        addons,
    ]);

    useEffect(() => {
        const { amount } = paymentData;
        const newAmount = amount - amount * (discount / 100);
        setAmountAfterDiscount("" + Math.ceil(newAmount));
    }, [discount]);

    useEffect(() => {
        setRedeemLoyaltyPoints(false);
        setRedeemedPoints(0);
    }, [loyaltyCustomer]);

    useEffect(() => {
        if (!loyalty?.hasProgram || !loyalty?.canRedeem) {
            setRedeemLoyaltyPoints(false);
            setRedeemedPoints(0);
            return;
        }

        if (redeemedPoints > loyalty.loyaltyPoints) {
            setRedeemedPoints(loyalty.loyaltyPoints);
        }
    }, [
        loyalty?.hasProgram,
        loyalty?.canRedeem,
        loyalty?.loyaltyPoints,
        redeemedPoints,
    ]);

    const handlePropertyChange = (event, propertyName, newValue) => {
        if (!newValue) newValue = event?.target.value;
        if (propertyName === "amount") {
            setAmountChanged(true);
        }
        setPaymentData((prevData) => ({
            ...prevData,
            [propertyName]: newValue,
        }));
    };

    const { mutate: createInvoiceMutation, isLoading: isCreatingInvoice } =
        useCrateOrderInvoice();
    const divideOrderItemsDataMutation = useDivideOrderItemsData();
    const onHouseOrderItemMutation = useOrderItemOnHouse();
    const defferedOrderItemMutation = useDefferOrderItem();
    const { showMasterPasswordDialog } = useMasterPassword();

    // CREATE INVOICE
    const handleInvoice = (
        orderId,
        payload,
        discountInfo = null,
        requiresElevatedPrivileges = false
    ) => {
        if (isCreatingInvoice) return;
        createInvoiceMutation(
            {
                orderId: orderId,
                formData: payload,
                elevatedPrivileges: requiresElevatedPrivileges,
                elevatedText: requiresElevatedPrivileges
                    ? "Apply discount to order"
                    : "",
            },
            {
                onSuccess: async (data) => {
                    // refetchFloorData();
                    const { invoice_name } = data.data.message;

                    // Update Table Order discount field after successful invoice creation
                    if (discountInfo) {
                        let tableOrderDiscount = 0;

                        if (
                            discountInfo.discountType === "fixed" &&
                            discountInfo.fixedDiscountAmount > 0
                        ) {
                            tableOrderDiscount =
                                discountInfo.fixedDiscountAmount;
                        } else if (
                            discountInfo.discountType === "percentage" &&
                            discountInfo.percentageDiscount > 0 &&
                            model?.doc?.entry_items
                        ) {
                            const originalTotal = model.doc.entry_items.reduce(
                                (total, item) => total + item.rate * item.qty,
                                0
                            );
                            tableOrderDiscount =
                                (originalTotal *
                                    discountInfo.percentageDiscount) /
                                100;
                        }

                        updateTableOrder.mutate({
                            doctype: "Table Order",
                            name: orderId,
                            formData: JSON.stringify({
                                discount: tableOrderDiscount,
                            }),
                        });
                    }

                    const settingsInvoicePrinter =
                        posProfile?.invoice_printer;
                    if (invoice_name) {
                        if (
                            !settingsInvoicePrinter &&
                            restaurantSettings?.docs[0]?.dont_print_invoices != 1
                        ) {
                            const printUrl = buildDownloadPdfUrl({
                                doctype: "POS Invoice",
                                name: invoice_name,
                                format: invoicePrintFormat,
                                noLetterhead: 0,
                                letterhead: selectedLetterhead,
                            });

                            // Create a hidden iframe to handle the PDF printing
                            const iframe = document.createElement("iframe");
                            iframe.style.display = "none";
                            iframe.src = printUrl;
                            document.body.appendChild(iframe);

                            // Wait for the iframe to load and then print
                            iframe.onload = () => {
                                try {
                                    iframe.contentWindow.print();
                                } catch (error) {
                                    console.error(
                                        "Error printing invoice:",
                                        error
                                    );
                                }
                                // Clean up the iframe after printing
                                // setTimeout(() => {
                                // document.body.removeChild(iframe);
                                // }, 1000);
                            };
                        }
                        if (handleInvoiceClosed) handleInvoiceClosed();
                    } else {
                        try {
                            const refreshedOrder =
                                await fetchTableOrderMutation.mutateAsync({
                                    name: orderId,
                                });
                            if (refreshedOrder?.link_invoice) {
                                if (handleInvoiceClosed) handleInvoiceClosed();
                                return;
                            }
                            enqueueSnackbar(
                                t("paymentWidget.invoiceReferenceMissing"),
                                { variant: "error" }
                            );
                            setPaymentLoading(false);
                        } catch (error) {
                            console.error(
                                "Unable to verify invoice creation:",
                                error
                            );
                            enqueueSnackbar(
                                t("paymentWidget.invoiceReferenceMissing"),
                                { variant: "error" }
                            );
                            // Keep checkout locked when the payment result cannot
                            // be verified, preventing a duplicate charge.
                            setPaymentLoading(true);
                        }
                    }
                },
                onError: (_err, _newTodo, context) => {
                    console.error("Error creating order invoice:", _err);
                    let msg = "Failed to create invoice";
                    try {
                        const serverMessages = _err?.response?.data?._server_messages;
                        if (serverMessages) {
                            const parsed = JSON.parse(serverMessages);
                            if (parsed.length > 0) {
                                msg = JSON.parse(parsed[0]).message || msg;
                            }
                        } else if (_err?.response?.data?.message) {
                            msg = _err.response.data.message;
                        } else if (_err?.message) {
                            msg = _err.message;
                        }
                    } catch (_) {
                        // Fallback to default message
                    }
                    enqueueSnackbar(msg, { variant: "error" });
                    setPaymentLoading(false);
                },
                onCancel: () => {
                    setPaymentLoading(false);
                },
            }
        );
    };

    // HERE
    // Print Table Order function (similar to PrintOrderModal)
    const printTableOrder = (orderId) => {
        const settingsInvoicePrinter =
            posProfile?.invoice_printer;

        if (!settingsInvoicePrinter) {
            // Print using iframe (browser printing)
            const printUrl = buildDownloadPdfUrl({
                doctype: "Table Order",
                name: orderId,
                format: tableOrderPrintFormat,
                noLetterhead: 0,
                letterhead: selectedLetterhead,
            });

            // Create a hidden iframe to handle the PDF printing
            const iframe = document.createElement("iframe");
            iframe.style.display = "none";
            iframe.src = printUrl;
            document.body.appendChild(iframe);

            // Wait for the iframe to load and then print
            iframe.onload = () => {
                try {
                    iframe.contentWindow.print();
                    // Mark order as printed
                    updateTableOrder.mutate({
                        doctype: "Table Order",
                        name: orderId,
                        formData: JSON.stringify({ invoice_printed: 1 }),
                    });
                } catch (error) {
                    console.error("Error printing order:", error);
                }
                // Clean up the iframe after printing
                // setTimeout(() => {
                //     document.body.removeChild(iframe);
                // }, 1000);
            };
        } else {
            // Print using hardware printer
            printOrder.mutate(
                {
                    orderId: orderId,
                    doctype: "Table Order",
                    printFormat: tableOrderPrintFormat,
                },
                {
                    onSuccess: (data) => {
                        console.log("Order printed successfully");
                        // Mark order as printed
                        updateTableOrder.mutate({
                            doctype: "Table Order",
                            name: orderId,
                            formData: JSON.stringify({ invoice_printed: 1 }),
                        });
                    },
                    onError: (e) => {
                        console.log("Error printing order:", e);
                    },
                }
            );
        }
    };

    const putOrderOnHouse = async (
        orderIdOverride,
        { preCollectedNotes, preCollectedMasterPassword } = {}
    ) => {
        try {
            // Skip the prompts when caller has already gathered them
            // (e.g. divide-first flow gathers up-front to keep the operation
            // atomic — see paymentConfirmed below).
            let notes = preCollectedNotes;
            if (notes === undefined) {
                notes = await showNotesPrompt({
                    title: "House Order Notes",
                    message:
                        "Please enter any notes for this house order (optional):",
                    placeholder: "Enter notes for house order...",
                });

                if (!notes) {
                    setPaymentLoading(false);
                    enqueueSnackbar("Notes are required", {
                        variant: "error",
                    });
                    return;
                }
            }

            // When the master password was collected up-front, pass it
            // directly and skip `elevatedPrivileges` so the hook does not
            // re-prompt.
            const orderId = orderIdOverride || model.doc.name;
            const mutationParams =
                preCollectedMasterPassword !== undefined
                    ? {
                          orderId,
                          notes: notes || "",
                          masterPassword: preCollectedMasterPassword,
                      }
                    : {
                          orderId,
                          notes: notes || "",
                          elevatedPrivileges:
                              elevatedPrivileges && approveOnCashClose,
                          elevatedText: "Put order on house",
                      };

            onHouseOrderItemMutation.mutate(
                mutationParams,
                {
                    onSuccess: (data) => {
                        refetchTablesData();
                        setPaymentLoading(false);
                        // Print the table order
                        printTableOrder(orderId);
                        if (handleInvoiceClosed) handleInvoiceClosed();
                    },
                    onError: (err) => {
                        setPaymentLoading(false);
                    },
                    onSettled: (data, error, variables, context) => {
                        setPaymentLoading(false);
                    },
                }
            );
        } catch (error) {
            // User cancelled the dialog - stop execution and reset loading state
            console.log("House order cancelled by user");
            setPaymentLoading(false);
            return; // Explicitly return to make cancellation handling clear
        }
    };

    const putOrderOnCustomer = (
        orderIdOverride,
        { preCollectedMasterPassword } = {}
    ) => {
        const orderId = orderIdOverride || model.doc.name;
        const mutationParams =
            preCollectedMasterPassword !== undefined
                ? {
                      orderId,
                      customer: selectedCustomer,
                      discount_percentage: discount,
                      masterPassword: preCollectedMasterPassword,
                  }
                : {
                      orderId,
                      customer: selectedCustomer,
                      discount_percentage: discount,
                      elevatedPrivileges:
                          elevatedPrivileges && approveOnCashClose,
                      elevatedText: "Defer order",
                  };
        defferedOrderItemMutation.mutate(
            mutationParams,
            {
                onSuccess: (data) => {
                    refetchTablesData();
                    setPaymentLoading(false);
                    setPaymentLoading(false);
                    printTableOrder(orderId);
                    if (handleInvoiceClosed) handleInvoiceClosed();
                },
                onError: (err) => {
                    setPaymentLoading(false);
                    console.log(err);
                },
                onSettled: (data, error, variables, context) => {
                    setPaymentLoading(false);
                },
            }
        );
    };

    const paymentConfirmed = async (modeOfPayment, discountInfo = null, meta = null) => {
        setPaymentLoading(true);

        if (posProfileCompanyMismatch?.message) {
            enqueueSnackbar(posProfileCompanyMismatch.message, {
                variant: "error",
            });
            setPaymentLoading(false);
            return;
        }

        const isProviderOrder = !!deliveryProviderName;
        const requestedRedeemedPoints =
            redeemLoyaltyPoints && redeemedPoints > 0 ? Number(redeemedPoints) : 0;

        const effectiveFixedDiscount = Number(
            discountInfo?.fixedDiscountAmount ?? fixedDiscountAmount ?? 0
        );
        const effectivePercentageDiscount = Number(
            discountInfo?.percentageDiscount ?? discountPercentage ?? discount ?? 0
        );
        const selectedDiscountPayload = buildDiscountPayload({
            fixedDiscountAmount: effectiveFixedDiscount,
            percentageDiscount: effectivePercentageDiscount,
        });
        const attemptedDiscount =
            effectiveFixedDiscount > 0 || effectivePercentageDiscount > 0;
        if (isProviderOrder && attemptedDiscount && !providerAllowsDiscount) {
            enqueueSnackbar(t("workMode.providerDiscountNotAllowed"), {
                variant: "error",
            });
            setPaymentLoading(false);
            return;
        }

        if (requestedRedeemedPoints > 0) {
            if (!payingAll) {
                enqueueSnackbar(t("paymentWidget.loyaltyFullOrderOnly"), {
                    variant: "error",
                });
                setPaymentLoading(false);
                return;
            }

            if (orderOnHouse || selectedCustomer || meta?.specialMode === "STAFF_MEAL") {
                enqueueSnackbar(t("paymentWidget.loyaltySpecialModeDisabled"), {
                    variant: "error",
                });
                setPaymentLoading(false);
                return;
            }
        }

        if (meta?.specialMode === "STAFF_MEAL") {
            try {
                if (!invoice || !order) {
                    setPaymentLoading(false);
                    return;
                }

                const employee = meta?.employee;
                if (!employee) {
                    enqueueSnackbar(t("paymentWidget.staffMealEmployeeRequired"), {
                        variant: "error",
                    });
                    setPaymentLoading(false);
                    return;
                }

                // When only a subset of items is being paid as staff meal,
                // split the order first so the staff meal invoice is created
                // only for the selected items (mirrors the regular pay /
                // OTH / Deferred flows). handle_staff_meal has no user
                // confirmation prompts, so it is safe to divide first here.
                let orderId = order.name;
                if (!payingAll) {
                    const invoiceEntriesPayload = {};
                    Object.entries(orderEntryItems).forEach(([key, value]) => {
                        invoiceEntriesPayload[key] = { ...value };
                    });
                    const dividePayload = {
                        client: "obj_3a5d-bf-457-825-0c237",
                        items: invoiceEntriesPayload,
                    };
                    const divideResp =
                        await divideOrderItemsDataMutation.mutateAsync({
                            orderId: order.name,
                            divisionPayload: dividePayload,
                        });
                    refetchTablesData();
                    const newOrderId = divideResp?.message?.name;
                    if (!newOrderId) {
                        setPaymentLoading(false);
                        enqueueSnackbar(
                            "Failed to divide order before applying Staff Meal",
                            { variant: "error" }
                        );
                        return;
                    }
                    orderId = newOrderId;
                }

                await handleStaffMealMutation.mutateAsync({
                    table_order: orderId,
                    mode_of_payment: modeOfPayment,
                    employee,
                });

                // handle_staff_meal already calls make_invoice on the backend,
                // so the invoice is created. Now do the same post-payment behaviour:
                // refetch tables, print the invoice, and close the dialog.
                refetchTablesData();

                // Reload Table Order to get the linked invoice name
                const updatedOrder = await fetchTableOrderMutation.mutateAsync({
                    name: orderId,
                });

                const invoiceName = updatedOrder?.link_invoice;
                if (invoiceName) {
                    const settingsInvoicePrinter =
                        posProfile?.invoice_printer;
                    if (
                        !settingsInvoicePrinter &&
                        restaurantSettings?.docs[0]?.dont_print_invoices != 1
                    ) {
                        const printUrl = buildDownloadPdfUrl({
                            doctype: "POS Invoice",
                            name: invoiceName,
                            format: invoicePrintFormat,
                            noLetterhead: 0,
                            letterhead: selectedLetterhead,
                        });
                        const iframe = document.createElement("iframe");
                        iframe.style.display = "none";
                        iframe.src = printUrl;
                        document.body.appendChild(iframe);
                        iframe.onload = () => {
                            try {
                                iframe.contentWindow.print();
                            } catch (error) {
                                console.error("Error printing invoice:", error);
                            }
                        };
                    }
                }

                setPaymentLoading(false);
                if (handleInvoiceClosed) handleInvoiceClosed();
                return;
            } catch (err) {
                console.error("Staff meal flow failed:", err);
                let msg = "Failed to apply staff meal";
                try {
                    const serverMessages = err?.response?.data?._server_messages;
                    if (serverMessages) {
                        const parsed = JSON.parse(serverMessages);
                        if (parsed.length > 0) {
                            msg = JSON.parse(parsed[0]).message || msg;
                        }
                    } else if (err?.response?.data?.message) {
                        msg = err.response.data.message;
                    } else if (err?.message) {
                        msg = err.message;
                    }
                } catch (_) {
                    // Fallback to default message
                }
                enqueueSnackbar(msg, { variant: "error" });
                setPaymentLoading(false);
                return;
            }
        }

        // When paying for a subset of items, OTH/Deferred must only apply to
        // the selected items — not the entire order. Mirror the make_invoice
        // flow by dividing the order first, then applying OTH/Deferred to
        // the newly created sub-order.
        //
        // IMPORTANT: gather any required user input (notes for OTH and the
        // master password for elevated privileges) BEFORE calling divide.
        // The divide endpoint mutates server state and is not reversible
        // from the client, so triggering it and then having the user cancel
        // a follow-up prompt would leave the order split with no matching
        // OTH/Deferred record.
        if ((orderOnHouse || selectedCustomer) && !payingAll) {
            let preCollectedNotes;
            if (orderOnHouse) {
                preCollectedNotes = await showNotesPrompt({
                    title: "House Order Notes",
                    message:
                        "Please enter any notes for this house order (optional):",
                    placeholder: "Enter notes for house order...",
                });
                if (!preCollectedNotes) {
                    setPaymentLoading(false);
                    enqueueSnackbar("Notes are required", {
                        variant: "error",
                    });
                    return;
                }
            }

            let preCollectedMasterPassword;
            if (elevatedPrivileges && approveOnCashClose) {
                try {
                    preCollectedMasterPassword = await showMasterPasswordDialog(
                        {
                            elevatedText: orderOnHouse
                                ? "Put order on house"
                                : "Defer order",
                        }
                    );
                } catch (_) {
                    setPaymentLoading(false);
                    return;
                }
            }

            const invoiceEntriesPayload = {};
            Object.entries(orderEntryItems).forEach(([key, value]) => {
                invoiceEntriesPayload[key] = { ...value };
            });

            const dividePayload = {
                client: "obj_3a5d-bf-457-825-0c237",
                items: invoiceEntriesPayload,
            };

            divideOrderItemsDataMutation.mutate(
                {
                    orderId: order.name,
                    divisionPayload: dividePayload,
                },
                {
                    onSuccess: (data) => {
                        refetchTablesData();
                        const newOrderId = data?.message?.name;
                        if (!newOrderId) {
                            setPaymentLoading(false);
                            enqueueSnackbar(
                                "Failed to divide order before applying OTH/Deferred",
                                { variant: "error" }
                            );
                            return;
                        }
                        if (orderOnHouse) {
                            putOrderOnHouse(newOrderId, {
                                preCollectedNotes,
                                preCollectedMasterPassword,
                            });
                        } else {
                            putOrderOnCustomer(newOrderId, {
                                preCollectedMasterPassword,
                            });
                        }
                    },
                    onError: () => {
                        setPaymentLoading(false);
                    },
                }
            );
            return;
        }

        if (orderOnHouse) {
            putOrderOnHouse();
            return;
        }

        if (selectedCustomer) {
            putOrderOnCustomer();
            return;
        }

        const getRequiresDiscountApproval = () => {
            if (!elevatedPrivileges) return false;

            if (effectiveFixedDiscount > 0) {
                return effectiveFixedDiscount > 1;
            }

            if (effectivePercentageDiscount > 0) {
                return effectivePercentageDiscount > 1;
            }

            return false;
        };

        // Check if discount operation requires elevated privileges
        const requiresElevatedPrivileges = getRequiresDiscountApproval();

        // Handle the new modeOfPayment object from AddPaymentWidget
        let cashItem = {};
        if (modeOfPayment && Object.keys(modeOfPayment).length > 0) {
            // Use the new payment mode data
            cashItem = modeOfPayment;
        } else {
            // Fallback to old logic for backward compatibility
            cashItem = { Cash: Number(paymentData.amount) };

            if (paymentType === "Credit Card") {
                cashItem = { "Credit Card": Number(paymentData.amount) };
            }
        }

        if (!invoice || !order) {
            enqueueSnackbar(t("paymentWidget.paymentDataNotReady"), {
                variant: "error",
            });
            setPaymentLoading(false);
            return;
        }

        const customer = invoice.customer;
        const orderId = order.name;
        const dinners = order.dinners || 1; //model.doc.dinners ?? 1;

        // Prepare discount data for Table Order API (matches backend make_invoice method signature)
        const payload = {
            mode_of_payment: cashItem,
            customer: customer,
            dinners: dinners,
            ...selectedDiscountPayload,
        };
        if (requestedRedeemedPoints > 0) {
            payload.redeem_loyalty_points = requestedRedeemedPoints;
        }

        const getCompanyAmountFromModeOfPaymentValue = (value) => {
            // Legacy payload: { "Cash": 50 } (already in company currency)
            if (typeof value === "number") return Number(value) || 0;

            // Foreign currency payload:
            // { "Cash USD": { amount: 20, currency: "USD", exchange_rate: 3.08 } }
            const amount = Number(value?.amount) || 0;
            const exchangeRate = Number(value?.exchange_rate) || 0;
            return exchangeRate ? amount * exchangeRate : amount;
        };

        // TODO: HERE
        if (!payingAll) {
            let invoiceEntriesPayload = {};

            Object.entries(orderEntryItems).forEach(([key, value]) => {
                invoiceEntriesPayload[key] = {
                    ...value,
                };
            });

            const dividePayload = {
                client: "obj_3a5d-bf-457-825-0c237",
                items: invoiceEntriesPayload,
            };

            divideOrderItemsDataMutation.mutate(
                {
                    orderId: order.name,
                    divisionPayload: dividePayload,
                },
                {
                    onSuccess: (data) => {
                        refetchTablesData();
                        setPaymentLoading(false);
                        const { message } = data;
                        const {
                            name,
                            entry_items: orderElements,
                            customer,
                        } = message;

                        if (orderElements && orderElements.length > 0 && name) {
                            let invoiceTotalAmount = 0;
                            orderElements.forEach((entry_item) => {
                                invoiceTotalAmount +=
                                    entry_item.qty * entry_item.rate;
                            });

                            // Handle mixed payments for divided orders too
                            let invoiceCashItem = {};
                            if (
                                modeOfPayment &&
                                Object.keys(modeOfPayment).length > 0
                            ) {
                                // Proportionally distribute the payment methods based on invoice amount
                                const totalPaidInCompanyCurrency = Object.values(
                                    modeOfPayment
                                ).reduce(
                                    (sum, value) =>
                                        sum +
                                        getCompanyAmountFromModeOfPaymentValue(
                                            value
                                        ),
                                    0
                                );
                                const ratio =
                                    totalPaidInCompanyCurrency > 0
                                        ? invoiceTotalAmount /
                                          totalPaidInCompanyCurrency
                                        : 0;

                                Object.entries(modeOfPayment).forEach(
                                    ([method, value]) => {
                                        if (typeof value === "number") {
                                            invoiceCashItem[method] = Number(
                                                (value * ratio).toFixed(2)
                                            );
                                            return;
                                        }
                                        if (
                                            value &&
                                            typeof value === "object"
                                        ) {
                                            invoiceCashItem[method] = {
                                                ...value,
                                                amount: Number(
                                                    (
                                                        (Number(value.amount) ||
                                                            0) * ratio
                                                    ).toFixed(2)
                                                ),
                                            };
                                        }
                                    }
                                );
                            } else {
                                // Fallback to old logic
                                invoiceCashItem = {
                                    Cash: Number(invoiceTotalAmount),
                                };

                                if (paymentType === "Credit Card") {
                                    invoiceCashItem = {
                                        "Credit Card":
                                            Number(invoiceTotalAmount),
                                    };
                                }
                            }

                            // Apply the same discount logic for POS Invoice
                            const invoicePayload = {
                                mode_of_payment: invoiceCashItem,
                                customer: customer,
                                dinners: 1,
                                ...selectedDiscountPayload,
                            };

                            handleInvoice(
                                name,
                                invoicePayload,
                                discountInfo,
                                requiresElevatedPrivileges
                            );
                        }
                    },
                    onError: (error) => {
                        console.error("Failed to divide order for payment:", error);
                        enqueueSnackbar(t("paymentWidget.prepareOrderFailed"), {
                            variant: "error",
                        });
                        setPaymentLoading(false);
                    },
                }
            );
        } else {
            setPaymentLoading(false);
            handleInvoice(
                orderId,
                payload,
                discountInfo,
                requiresElevatedPrivileges
            );
        }
    };

    const handleBackCallback = () => {
        setAmountChanged(false);
        setLoadingTable("");
        setAmountChanged(false);
        backCallback?.();
        // if (handleInvoiceClosed) handleInvoiceClosed();
    };

    return (
        <>
            {mode ? (
                <AddPaymentWidget
                    selectedTip={tip}
                    onUpdateTip={(e) => setTip(e)}
                    onUpdateDiscount={(e) => {
                        setDiscount(e);
                    }}
                    invoice={invoice}
                    presetDiscount={presetDiscount}
                    presetDiscountAmount={presetDiscountAmount}
                    selectedDiscount={discount}
                    onConfirm={paymentConfirmed}
                    isLoading={isPaymentLoading}
                    handleNext={handleNext}
                    orderAmount={paymentData.orderAmount}
                    order={order}
                    amountAfterDiscount={amountAfterDiscount}
                    isCreatingInvoice={isCreatingInvoice}
                    handlePropertyChange={handlePropertyChange}
                    isNew={!amountChanged}
                    payingAll={payingAll}
                    backCallback={handleBackCallback}
                    paymentType={paymentType}
                    setPaymentType={setPaymentType}
                    orderOnHouse={orderOnHouse}
                    setOrderOnHouse={setOrderOnHouse}
                    setSelectedCustomer={setSelectedCustomer}
                    selectedCustomer={selectedCustomer}
                    isTakeaway={isTakeaway}
                    fixedDiscountAmount={fixedDiscountAmount}
                    setFixedDiscountAmount={setFixedDiscountAmount}
                    discountPercentage={discountPercentage}
                    setDiscountPercentage={setDiscountPercentage}
                    staffMealEmployees={staffMealEmployees || []}
                    loyalty={loyalty}
                    hasActiveLoyaltyProgram={hasActiveLoyaltyProgram}
                    isLoyaltyLoading={loyaltyQuery.isLoading || loyaltyQuery.isFetching}
                    loyaltyError={loyaltyQuery.error}
                    redeemLoyaltyPoints={redeemLoyaltyPoints}
                    setRedeemLoyaltyPoints={setRedeemLoyaltyPoints}
                    redeemedPoints={redeemedPoints}
                    setRedeemedPoints={setRedeemedPoints}
                    loyaltyAmount={loyaltyAmount}
                    onStaffMealEmployeeChange={(employee) => {
                        if (employee) {
                            // Re-fetch invoice with employee to show staff meal discount preview
                            fetchGetInvoiceData(
                                {
                                    orderId: activeOrderName,
                                    entry_items: orderEntryItems || {},
                                    discount_percentage: 0,
                                    employee,
                                },
                                {
                                    onSuccess: (data) => {
                                        setInvoice(data);
                                        // Show server warnings (e.g. "Employee has already had a staff meal today")
                                        if (data?._server_messages) {
                                            try {
                                                const parsed = JSON.parse(data._server_messages);
                                                if (parsed.length > 0) {
                                                    const msg = JSON.parse(parsed[0]).message;
                                                    if (msg) {
                                                        enqueueSnackbar(msg, { variant: "warning" });
                                                    }
                                                }
                                            } catch (_) {}
                                        }
                                    },
                                }
                            );
                        } else {
                            // Employee deselected, re-fetch invoice without employee
                            fetchGetInvoiceData(
                                {
                                    orderId: activeOrderName,
                                    entry_items: orderEntryItems || {},
                                    discount_percentage: discount,
                                },
                                {
                                    onSuccess: (data) => {
                                        setInvoice(data);
                                    },
                                }
                            );
                        }
                    }}
                />
            ) : isAutoAdvancing ||
              isInvoiceLoading ||
              isInvoiceDataLoading ||
              isAddonsFetching ? (
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "3rem",
                        minWidth: 240,
                    }}
                >
                    <CircularProgress />
                </div>
            ) : autoAdvanceError || isAddonsError ? (
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "1rem",
                        padding: "2rem",
                        minWidth: 300,
                    }}
                >
                    <Alert severity="error">
                        {t("paymentWidget.paymentDetailsLoadFailed")}
                    </Alert>
                    <Button
                        variant="contained"
                        onClick={() => {
                            setAutoAdvanceError(false);
                            autoAdvancedRef.current = false;
                            if (isAddonsError) {
                                refetchAddons();
                            }
                        }}
                    >
                        {t("common.retry")}
                    </Button>
                </div>
            ) : (
                <OrdersListWidget
                    entryItems={entryItems}
                    handleNext={callHandleNext}
                    loading={isInvoiceDataLoading}
                    backCallback={handleBackCallback}
                    isInvoiceLoading={isInvoiceLoading}
                    table={table}
                />
            )}
        </>
    );
}
